import { FC } from 'react'
import classes from './responseRateChart.module.scss'

export interface ResponseRateDatum {
  answered: number
  id: string
  name: string
  unanswered: number
}

interface ResponseRateChartProps {
  data: ResponseRateDatum[]
}

const TICK_COUNT = 8

// Round a raw max up to a "nice" step so the axis lands on clean numbers.
const getNiceStep = (max: number, ticks: number): number => {
  const rough = max / ticks
  const magnitude = 10 ** Math.floor(Math.log10(rough))
  const normalized = rough / magnitude
  let niceNormalized = 1
  if (normalized > 5) niceNormalized = 10
  else if (normalized > 2) niceNormalized = 5
  else if (normalized > 1) niceNormalized = 2
  return niceNormalized * magnitude
}

export const ResponseRateChart: FC<ResponseRateChartProps> = ({ data }) => {
  if (!data.length) {
    return (
      <div className={classes.empty}>No response rate data available yet.</div>
    )
  }

  const maxTotal = data.reduce(
    (largest, d) => Math.max(largest, d.answered + d.unanswered),
    0
  )
  const step = getNiceStep(Math.max(maxTotal, 1), TICK_COUNT)
  const xMax = Math.max(step * TICK_COUNT, step)
  const ticks = Array.from({ length: TICK_COUNT + 1 }, (_, i) => i * step)

  return (
    <div className={classes.chart}>
      <div className={classes.axisRow}>
        <div className={classes.labelCol} />
        <div className={classes.axisTicks}>
          {ticks.map((tick, i) => (
            <span
              className={classes.axisTick}
              key={tick}
              style={{ left: `${(i / TICK_COUNT) * 100}%` }}
            >
              {tick}
            </span>
          ))}
        </div>
        <div className={classes.countCol} />
      </div>

      <div className={classes.rows}>
        {data.map((d) => {
          const answeredWidth = (d.answered / xMax) * 100
          const unansweredWidth = (d.unanswered / xMax) * 100
          const hasAnswered = d.answered > 0
          const hasUnanswered = d.unanswered > 0

          return (
            <div className={classes.row} key={d.id}>
              <div className={classes.rowLabel} title={d.name}>
                {d.name}
              </div>

              <div className={classes.plot}>
                <div className={classes.gridLines}>
                  {ticks.map((tick, i) => (
                    <span
                      className={classes.gridLine}
                      key={tick}
                      style={{ left: `${(i / TICK_COUNT) * 100}%` }}
                    />
                  ))}
                </div>

                <div className={classes.track}>
                  {hasAnswered && (
                    <div
                      className={classes.segment}
                      style={{
                        background: '#8080FF',
                        borderRadius: hasUnanswered
                          ? '100px 0 0 100px'
                          : '100px',
                        width: `${answeredWidth}%`,
                      }}
                    />
                  )}
                  {hasUnanswered && (
                    <div
                      className={classes.segment}
                      style={{
                        background: '#FF6A33',
                        borderRadius: hasAnswered ? '0 100px 100px 0' : '100px',
                        width: `${unansweredWidth}%`,
                      }}
                    />
                  )}
                </div>
              </div>

              <div className={classes.rowCount}>
                {d.answered} / {d.unanswered}
              </div>
            </div>
          )
        })}
      </div>

      <div className={classes.legend}>
        <span className={classes.legendItem}>
          <span
            className={classes.legendDot}
            style={{ background: '#8080FF' }}
          />
          Answered
        </span>
        <span className={classes.legendItem}>
          <span
            className={classes.legendDot}
            style={{ background: '#FF6A33' }}
          />
          Unanswered
        </span>
      </div>
    </div>
  )
}
